import { Injectable } from '@angular/core';
import { AuthService, Token } from '@app/auth/services/auth.service';

import { ApiOrderService, Order } from '@shared/services/cart/api-order.service';
import { snakeCase } from 'lodash';

import { iif, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Basket } from './api-basket.service';
import { SessionStorageService } from '../storage';

class ShippingTypes {
  TYPE_EU_DELIVERY = 'eu_delivery';
  TYPE_WORLD_DELIVERY = 'world_delivery';
  TYPE_DOMESTIC_DELIVERY = 'domestic_delivery';
  TYPE_COLLECTION_ON_SITE = 'collection_on_site';
}

export enum ShippingPackageType {
  PAY_EACH_INVENTORY = 1,
  PAY_FOR_EXPENSIVE_SHIPPING = 2,
}

/**
 * Czy klient rozlicza transport za każdą sztukę osobno (tryb 1).
 *
 * Backend trzyma tryb w kolumnie tekstowej, więc do koszyka trafia '1' albo '2' — porównanie
 * musi znosić i string, i liczbę. Predykat jest wspólny dla wszystkich komponentów koszyka:
 * trzy osobne warunki potrafiły rozjechać się tak, że jeden komponent liczył trybem 1,
 * a drugi trybem 2 w tym samym koszyku.
 */
export function isPerItemShipping(shippingPackageType: string | number): boolean {
  return String(shippingPackageType) === String(ShippingPackageType.PAY_EACH_INVENTORY);
}

export interface ShippingItem {
  type: string;
  objectId: number;
  quantity: number;
  price: number;
  currency: string;
}

export interface ShippingType {
  type: string;
  key: string;
  price: string;
}

export interface ShippingPackageInfo {
  maximumElementsInPackage: string;
  type: string;
}

export interface OrderCost {
  insurance: string;
  items: ShippingItem[];
  shippingTypes: ShippingType[];
  shippingPackage: ShippingPackageInfo;
  individual: boolean;
}

@Injectable()
export class OrderService {
  constructor(
    private authService: AuthService,
    private apiOrder: ApiOrderService

  ) {
  }

  shoppingCost(basket: Basket): Observable<OrderCost> {
    return this.authService.authState$.pipe(
      switchMap((state) =>
        iif(
          () => this.condition(state),
          this.shoppingCostBasedOnBasket$(),
          this.shoppingCostBasedOnLocalBasket$(basket)
        )
      )
    );
  }

  submitOrder(order: any, basket: any): Observable<any> {
    return this.authService.authState$.pipe(
      switchMap((state) =>
        iif(
          () => this.condition(state),
          this.submit$(order),
          this.submitUnauthorized$(order, basket)
        )
      )
    );
  }

  private condition = (state: Token | null | undefined) =>
    state !== undefined && state !== null;

  private submit$(order: any): Observable<any> {
    return this.apiOrder.submit(order);
  }

  private submitUnauthorized$(orderForm: any, basket: any): Observable<any> {
    const {
      register_user,
      password,
      accept_privacy_policy_regulations,
      accept_newsletter,
      ...order
    } = orderForm;

    const register_data = register_user
      ? {
        password,
        register_user,
        accept_privacy_policy_regulations,
        accept_newsletter,
      }
      : {};

    return this.apiOrder.submitUnauthorized({
      order,
      basket,
      ...register_data
    });
  }

  private shoppingCostBasedOnBasket$(): Observable<OrderCost> {
    return this.apiOrder
      .orderBaseOnUserBasket()
      .pipe(map(this.orderToShippingCost));
  }

  private shoppingCostBasedOnLocalBasket$(
    basket: Basket
  ): Observable<OrderCost> {
    return this.apiOrder
      .orderBaseOnBasket(basket)
      .pipe(map(this.orderToShippingCost));
  }

  private orderToShippingCost(order: Order): OrderCost {
    const { shippingCosts, items } = order;
    const {
      shippingPackageMaximumElementsInPackage,
      shippingPackageType,
    } = order;
    const { insurance, ...rest } = shippingCosts;

    const shippingTypes = Object.entries(rest)
      .sort()
      .map(([type, price]) => ({ key: snakeCase(type), type, price }));

    const _shippingCost: OrderCost = {
      insurance: insurance,
      shippingTypes: shippingTypes,
      items: items,
      individual: false,
      shippingPackage: {
        maximumElementsInPackage: shippingPackageMaximumElementsInPackage,
        type: shippingPackageType,
      },
    };

    return _shippingCost;
  }
}
